import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NavController, NavParams, ViewController } from "ionic-angular";
import { Order, OrderComponent } from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import { ScriptSureDrug } from "models/scriptSure/scriptsureDrug";
import { ScriptSureDrugsRequest } from "models/scriptSure/scriptsureDrugsRequest";
import { ScriptSureDrugsResponse } from "models/scriptSure/scriptsureDrugsResponse";
import { OrderFormState } from "pages/orders-entry-om/add-order/order-form-state";
import { MedicationDataService } from "providers/order-data-service/medications-data-service";
import { OrderManagementDataService } from "providers/order-management-data-service/order-management-data-service";
import { Observable, Subject } from "rxjs";
import { interval } from "rxjs/observable/interval";
import { of } from "rxjs/observable/of";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  mergeMap,
  switchMap,
  take,
  tap,
} from "rxjs/operators";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import {
  OrderComponentInterface,
  OrderComponentPropertyUpdate,
  OrderCreateRequestUpdate,
} from "../order-components-interface";

@Component({
  selector: "medication-name-description-scriptsure",
  templateUrl: "medication-name-description-scriptsure.html",
})
export class MedicationNameDescriptionScriptSureComponent
  implements OrderComponentInterface
{
  /****************************************************************************
   * Inputs                                                                   *
   ****************************************************************************/
  @Input() orderComponent: OrderComponent;
  @Input() orderCreateRequest: Order;
  @Input() set orderFormState(orderFormState: OrderFormState) {
    this._orderFormState = orderFormState;
    if (this._orderFormState.isCompound !== this.prevIsCompound) {
      this.prevIsCompound = this._orderFormState.isCompound;
      if (!this._orderFormState.isCompound) {
        this.selectedNameDescription = 0;
        if (
          this.orderCreateRequest.nameDescription &&
          this._orderFormState.formType === "convertToOrder"
        ) {
          this.prefillDescription(this.orderCreateRequest.nameDescription);
        }
      }
    }
    this.checkIsValid();
  }

  /****************************************************************************
   * Outputs                                                                  *
   ****************************************************************************/
  @Output() orderCreateRequestEvent =
    new EventEmitter<OrderCreateRequestUpdate>();
  @Output() orderFormStateEvent = new EventEmitter<OrderFormState>();
  @Output() isValidEvent = new EventEmitter<boolean>();
  @Output() amendedOrderRequestEvent =
    new EventEmitter<OrderComponentPropertyUpdate>();

  /****************************************************************************
   * ViewChild                                                                *
   ****************************************************************************/
  @ViewChild("medicationNameId") medicationNameInput: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  /****************************************************************************
   * State                                                                    *
   ****************************************************************************/
  _orderFormState: OrderFormState = {};
  existingMedOrders: Array<StatefulOrderResponse>;
  selectedNameDescription: number;
  medications$: Observable<Array<ScriptSureDrug>> = of([]);
  medicationInput$ = new Subject<string>();
  medicationsLoading = false;
  prevIsCompound: boolean = false;
  dosageForm: string;
  dosageRoute: string;
  dosageStrength: string;
  duplicatedMed: boolean = false;

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    public viewCtrl: ViewController,
    public stateData: StateManagementService,
    public medicationDataService: MedicationDataService,
    private orderManagementDataService: OrderManagementDataService
  ) {}

  /****************************************************************************
   * Lifecycle Methods                                                        *
   ****************************************************************************/
  ngOnInit() {
    this.checkIsValid();
    this.loadExistingMedicationOrders();

    this.medications$ = this.medicationInput$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      tap(() => (this.medicationsLoading = true)),
      switchMap((term) => {
        // null -> clear, '' -> field cleared by backspace
        if (!term || term.length === 0) {
          return of([]);
        }
        return this.medicationDataService
          .getMedications(this.searchInputToScriptSureSearchRequest(term))
          .pipe(
            mergeMap((scriptSureDrugsResponse) =>
              of(scriptSureDrugsResponse.drugs)
            ),
            catchError(() => of([])), // empty list on error
            tap(() => (this.medicationsLoading = false))
          );
      })
    );
  }

  /****************************************************************************
   * Getter Methods                                                           *
   ****************************************************************************/
  get notFoundText(): string {
    return this.medicationsLoading
      ? "Searching medications..."
      : "No medications found.";
  }

  getScriptSureRequest(term: any): ScriptSureDrugsRequest {
    return {
      term,
      searchMedication: true,
      searchIndication: false,
      searchOtc: true,
      searchSupply: true,
      searchBrand: true,
      searchGeneric: true,
      includeCode: true,
      searchStatus: 0,
    };
  }

  /****************************************************************************
   * Setter Methods                                                           *
   ****************************************************************************/
  setAmendedValue() {
    this.medicationDataService
      .getMedications(
        this.getScriptSureRequest(this.orderCreateRequest.nameDescription)
      )
      .subscribe(
        (response: ScriptSureDrugsResponse) => {
          this.medicationInput$.next(this.orderCreateRequest.nameDescription);
          const med = response.drugs.find(
            (med) =>
              med.MED_ROUTED_MED_ID_DESC ===
              this.orderCreateRequest.nameDescription
          );
          this.selectedNameDescription = med.MED_NAME_ID;
          this.onMedicationSelected(med);
          this.checkIsValid();
        },
        (error) => {
          console.error("Error when searching for medication options", error);
        }
      );
  }

  /****************************************************************************
   * Component Related Methods                                                *
   ****************************************************************************/

  // Check for UI element to appear on the screen
  prefillDescription(prefill: string) {
    const checkIntervalMs = 500; // Adjust as necessary
    const maxRetries = 20; // Maximum number of checks before giving up

    interval(checkIntervalMs)
      .pipe(
        take(maxRetries),
        filter(() => !!this.medicationNameInput)
      )
      .subscribe(() => {
        this.medicationNameInput?.filter(prefill);
      });
  }

  cannotBeNA() {
    //validator used to ensure that some fields cannot be 'N/A'
    const naRegex = /^n\/?a$/i;
    return (control: AbstractControl): { [key: string]: any } | null => {
      const isNA = naRegex.test(control.value);
      return isNA ? { cannotBeNA: { value: control.value } } : null;
    };
  }
  set SelectedNameDescription(value: any) {
    this.selectedNameDescription = value;
  }

  searchInputToScriptSureSearchRequest(input: string): ScriptSureDrugsRequest {
    return {
      term: input,
      searchMedication: true,
      searchIndication: false,
      searchOtc: true,
      searchSupply: true,
      searchBrand: true,
      searchGeneric: true,
      includeCode: true,
      searchStatus: 0,
    };
  }

  onMedicationSelected(drug: ScriptSureDrug) {
    if (!drug) {
      this.orderFormStateEvent.emit({
        nameDescription: "",
        medRouteId: undefined,
        routedMedDescription: "",
      });
      return;
    }
    //Name description is composed of med name + strength, so we need to extract the strength to compare only the medication name
    this.duplicatedMed = this.existingMedOrders.some(
      (med) =>
        med.nameDescription.replace(" - " + med.dosageStrength, "") ===
        drug.MED_ROUTED_MED_ID_DESC
    );

    if (this._orderFormState.formType === "amendment") {
      const componentField = this.orderComponent.properties[0].name;
      const amendedObject: OrderComponentPropertyUpdate = { updates: {} };
      if (
        drug.MED_ROUTED_MED_ID_DESC !== this.orderCreateRequest.nameDescription
      ) {
        amendedObject.updates = {
          propertyName: componentField,
          previousValueDisplay: this.orderCreateRequest[componentField],
          previousValue: this.orderCreateRequest[componentField],
          newValue: drug.MED_ROUTED_MED_ID_DESC,
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      } else {
        const amendedObject: OrderComponentPropertyUpdate = {
          updates: {},
          deletes: ["nameDescription"],
        };
        this.amendedOrderRequestEvent.emit(amendedObject);
      }
    } else {
      // Don't emit for compound medications
      if (!this._orderFormState.isCompound) {
        this.orderCreateRequestEvent.emit({
          updates: {
            nameDescription: drug.MED_ROUTED_MED_ID_DESC,
          },
        });
      }
    }
    this.orderFormStateEvent.emit({
      medRouteId: Number(drug.ROUTED_MED_ID),
      routedMedDescription: drug.MED_ROUTED_MED_ID_DESC,
    });
    this.checkIsValid();
  }

  checkIsValid() {
    if (this._orderFormState.isCompound) {
      this.isValidEvent.emit(true);
      return;
    }
    if (
      this.orderComponent.properties[0].required &&
      (!this.selectedNameDescription || this.selectedNameDescription === 0)
    ) {
      this.isValidEvent.emit(false);
    } else {
      this.isValidEvent.emit(true);
    }
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClick() {
    this.hiddenInput.setFocus();
    this.medicationNameInput.filterInput.nativeElement.focus();
  }

  disableField() {
    // If existing medication orders have not yet loaded
    if (!this.existingMedOrders) {
      return true;
    }
    return (
      !this.orderComponent.properties[0].amendable &&
      this._orderFormState.formType === "amendment"
    );
  }

  loadExistingMedicationOrders() {
    this.orderManagementDataService
      .searchOrders({
        patientChartId: this._orderFormState.patientChartId,
        orderType: "MEDICATION",
        from: 0,
        size: 100,
        includeDiscontinued: false,
      })
      .pipe(
        mergeMap((orders) => {
          return of(orders.hits ? orders.hits : []);
        })
      )
      .subscribe((orders) => {
        this.existingMedOrders = orders;
        if (
          (this._orderFormState.formType === "amendment" ||
            this._orderFormState.formType === "convertToOrder") &&
          !this.orderCreateRequest.isCompound
        ) {
          this.setAmendedValue();
        }
      });
  }

  onClear() {
    this.orderFormStateEvent.emit({
      nameDescription: "",
      medRouteId: undefined,
    });
  }

  /****************************************************************************
   * Cypress-Related ("data-cy") Methods                                      *
   ****************************************************************************/
  toKebabCase(label: string): string {
    if (label) {
      return label
        .replace(/^\s+|\s+$/gm, "") // Trim leading and trailing whitespace
        .replace(/([a-z])([A-Z])/g, "$1-$2") // Add hyphen between camelCase words
        .replace(/[\s_]+/g, "-") // Replace spaces and underscores with hyphens
        .replace(/[^\w-]+/g, "") // Remove punctuation (non-word characters except hyphen)
        .toLowerCase(); // Convert to lowercase
    }
    return "";
  }

  createDataCyAttribute(component: string, option: string): string {
    return `${this.toKebabCase(component)}-option-${this.toKebabCase(option)}`;
  }
}



// WEBPACK FOOTER //
// ./src/components/order-management/medication-name-description-scriptsure/medication-name-description-scriptsure.ts